import { Inject, Injectable } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SalesTeamService } from '@vendasta/sales';
import {
  CustomerSalesOrdersService,
  InitiateOrderExportRequest,
  InitiateOrderExportRequestDataFormatOption,
  InitiateOrderExportRequestFilters,
  InitiateOrderExportRequestFiltersInterface,
  InitiateOrderExportRequestSortDirection,
  InitiateOrderExportRequestSortField,
  InitiateOrderExportRequestSortOption,
  InitiateOrderExportRequestSortOptionInterface,
  Status,
} from '@vendasta/sales-orders';
import { ExportToCSVRequest, SortDirection, SortSetting } from '@vendasta/va-filter2-table';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PARTNER_ID_TOKEN } from '../external-dependencies/provider-tokens';
import {
  BaseSalesOrdersExportAllService,
  ForcedFilterOptions,
  RequestedExportType,
  exportDataFormatModalOption,
} from './interface';

@Injectable()
export class SalesOrdersExportAllService implements BaseSalesOrdersExportAllService<void> {
  // forced filters apply when the end user is only able to view certain data (salesperson, smb has different context than partner)
  private forcedFilterOptions: ForcedFilterOptions;
  private exportOptions: RequestedExportType;

  constructor(
    private readonly salesOrderService: CustomerSalesOrdersService,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    private readonly salesTeamService: SalesTeamService,
    private readonly alertService: SnackbarService,
  ) {}

  static convertSortOptions(sort: SortSetting[]): InitiateOrderExportRequestSortOption | null {
    let s: InitiateOrderExportRequestSortOptionInterface = null;
    if (!!sort && sort.length > 0) {
      s = {};
      s.direction =
        sort[0].direction === SortDirection.SORT_DIRECTION_ASCENDING
          ? InitiateOrderExportRequestSortDirection.ASCENDING
          : InitiateOrderExportRequestSortDirection.DESCENDING;
      switch (sort[0].field) {
        case 'status':
          s.field = InitiateOrderExportRequestSortField.STATUS;
          break;
        case 'requestedActivation':
          s.field = InitiateOrderExportRequestSortField.REQUESTED_ACTIVATION;
          break;
        case 'created':
          s.field = InitiateOrderExportRequestSortField.CREATED;
          break;
        case 'expiry':
          s.field = InitiateOrderExportRequestSortField.EXPIRY_DATE;
          break;
        default:
          s = null;
      }
    }
    if (s) {
      return new InitiateOrderExportRequestSortOption(s);
    }
    return null;
  }

  static convertFilters(
    filters: any,
    filteredTeamSalesPersonIds: string[],
    forcedMarkets?: string[],
    forcedSalesperson?: string,
    forcedBusinessId?: string,
  ): InitiateOrderExportRequestFilters | null {
    const f: InitiateOrderExportRequestFiltersInterface = {};
    f.statuses = [];
    f.created = {};
    f.requestedActivation = {};
    f.expiryDate = {};

    // status filters
    if (filters.controls['activated'].value) {
      f.statuses.push(Status.FULFILLED);
    }
    if (filters.controls['activation-errors'].value) {
      f.statuses.push(Status.ACTIVATION_ERRORS);
    }
    if (filters.controls['approved'].value) {
      f.statuses.push(Status.APPROVED);
    }
    if (filters.controls['archived'].value) {
      f.statuses.push(Status.ARCHIVED);
    }
    if (filters.controls['awaiting-customer-approval'].value) {
      f.statuses.push(Status.SUBMITTED_FOR_CUSTOMER_APPROVAL);
    }
    if (filters.controls['declined'].value) {
      f.statuses.push(Status.DECLINED);
    }
    if (filters.controls['pending'].value) {
      f.statuses.push(Status.SUBMITTED);
    }
    if (filters.controls['processing'].value) {
      f.statuses.push(Status.PROCESSING);
    }
    if (filters.controls['drafted'].value) {
      f.statuses.push(Status.DRAFTED);
    }
    if (filters.controls['cancellation-requested'].value) {
      f.statuses.push(Status.CANCELLATION_REQUESTED);
    }
    if (filters.controls['cancelled'].value) {
      f.statuses.push(Status.CANCELLED);
    }
    if (filters.controls['scheduled-activation'].value) {
      f.statuses.push(Status.SCHEDULED_ACTIVATION);
    }

    // date filters
    if (filters.controls['submittedDateFromControl'].value) {
      f.created.beginRange = filters.controls['submittedDateFromControl'].value;
    }
    if (filters.controls['submittedDateToControl'].value) {
      f.created.endRange = filters.controls['submittedDateToControl'].value;
    }

    if (filters.controls['contractStartFrom'].value) {
      f.requestedActivation.beginRange = filters.controls['contractStartFrom'].value;
    }
    if (filters.controls['contractStartToControl'].value) {
      f.requestedActivation.endRange = filters.controls['contractStartToControl'].value;
    }

    if (filters.controls['expiryDateFrom'].value) {
      f.expiryDate.beginRange = filters.controls['expiryDateFrom'].value;
    }
    if (filters.controls['expiryDateTo'].value) {
      f.expiryDate.endRange = filters.controls['expiryDateTo'].value;
    }

    // other filters
    if (filters.controls['tag'].value) {
      f.tags = [filters.controls['tag'].value];
    }
    if (filters.controls['untagged'].value) {
      f.untagged = true;
    }
    if (filters.controls['orderId'].value) {
      f.orderId = filters.controls['orderId'].value;
    }

    if (filters.controls['package']?.value) {
      f.packageIds = [filters.controls['package'].value.packageId];
    }

    // forced filters;  based on which client the table is being viewed
    if (forcedMarkets) {
      // Salesperson view has a forced market filter
      f.marketIds = forcedMarkets;
    } else if (filters.controls['market'].value) {
      f.marketIds = [filters.controls['market'].value.marketId];
    }

    if (forcedBusinessId) {
      // SMB view has a forced business filter
      f.businessIds = [forcedBusinessId];
    } else if (filters.controls['businessId'].value) {
      f.businessIds = [filters.controls['businessId'].value];
    }

    if (forcedSalesperson) {
      // Some Salesperson views have access to more than the current Salesperson
      f.salespersonIds = [forcedSalesperson];
    } else if (filters.controls['salesPeople'].value) {
      const salespersonId = filters.controls['salesPeople'].value.salespersonId;
      // currently uses the selected sales team to append additional salesperson IDs from what may have been selected
      f.salespersonIds = filteredTeamSalesPersonIds.includes(salespersonId)
        ? filteredTeamSalesPersonIds
        : [...filteredTeamSalesPersonIds, salespersonId];
    } else if (filters.controls['salesTeam'].value) {
      f.salespersonIds = filteredTeamSalesPersonIds.length === 0 ? [] : filteredTeamSalesPersonIds;
    }

    if (f) {
      return new InitiateOrderExportRequestFilters(f);
    }
    return null;
  }

  // initializes the service with forced filters based on which client is viewing the table
  // eg. SMBs in Business Center have a forced filter for businessId
  //     Salespeople in Sales Center have a forced filter for marketId
  initialize(forcedFilterOptions: ForcedFilterOptions, exportOptions: RequestedExportType): void {
    this.forcedFilterOptions = forcedFilterOptions;
    this.exportOptions = exportOptions;
  }

  // Gets the export data format based on:
  // 1. User selection from the download modal
  // 2. Which user type has made the request, based on the forced filter specified
  //    Eg. Business Center and Sales Center Users have different access to which data/columns should be displayed in the file
  getSelectedDataFormat(fileFormatSelected: string): InitiateOrderExportRequestDataFormatOption {
    let selectedDataFormat = InitiateOrderExportRequestDataFormatOption.ORDERS_PER_ROW;
    if (fileFormatSelected === exportDataFormatModalOption.PRODUCTS_PER_ROW) {
      selectedDataFormat = InitiateOrderExportRequestDataFormatOption.PRODUCTS_PER_ROW;
    }

    if (this.exportOptions === RequestedExportType.Buyer) {
      if (fileFormatSelected === exportDataFormatModalOption.PRODUCTS_PER_ROW) {
        selectedDataFormat = InitiateOrderExportRequestDataFormatOption.PRODUCTS_PER_ROW_BUYER;
      } else {
        selectedDataFormat = InitiateOrderExportRequestDataFormatOption.ORDERS_PER_ROW_BUYER;
      }
    } else if (this.exportOptions === RequestedExportType.Sales) {
      if (fileFormatSelected === exportDataFormatModalOption.PRODUCTS_PER_ROW) {
        selectedDataFormat = InitiateOrderExportRequestDataFormatOption.PRODUCTS_PER_ROW_SALES;
      } else {
        selectedDataFormat = InitiateOrderExportRequestDataFormatOption.ORDERS_PER_ROW_SALES;
      }
    }
    return selectedDataFormat;
  }

  exportAllRowsToCSV(request: ExportToCSVRequest, option?: string): void {
    const selectedDataFormat = this.getSelectedDataFormat(option);

    // prepare sort and filters
    const sortOptions = SalesOrdersExportAllService.convertSortOptions(request.sortingOptions);
    let salesPersonIdsBelongingToFilteredTeam$ = of([]);
    if (request.filters?.controls['salesTeam'].value) {
      salesPersonIdsBelongingToFilteredTeam$ = this.salesTeamService
        .getMembers(request.filters.controls['salesTeam'].value.id)
        .pipe(catchError(() => of([])));
    }
    const filters$ = salesPersonIdsBelongingToFilteredTeam$.pipe(
      map((salesPersonIdsBelongingToFilteredTeam) => {
        const teamSalesPersonIds = (salesPersonIdsBelongingToFilteredTeam || []).map((sp) => sp.salespersonId);
        return SalesOrdersExportAllService.convertFilters(
          request.filters,
          teamSalesPersonIds,
          this.forcedFilterOptions.marketIds,
          this.forcedFilterOptions.salespersonId,
          this.forcedFilterOptions.businessId,
        );
      }),
    );

    // make export request
    combineLatest([this.partnerId$, filters$])
      .pipe(
        switchMap(([partnerId, filters]) => {
          const tableUrlWithFiltersSet = window.location.href;
          const exportOrderRequest = new InitiateOrderExportRequest({
            partnerId: partnerId,
            filters: filters,
            sortOption: sortOptions,
            tryAgainUrl: tableUrlWithFiltersSet,
            selectedDataFormat: selectedDataFormat,
          });
          return this.salesOrderService.initiateOrderExport(exportOrderRequest);
        }),
      )
      .subscribe({
        next: () => {
          this.alertService.openSuccessSnack('FRONTEND.SALES_UI.EXPORT.SUCCESS_EXPORT_ALL');
        },
        error: (err) => {
          if (err.status === 429) {
            this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_EXPORT_TOO_MANY_REQUESTS');
          } else {
            this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_EXPORT_ALL');
          }
        },
      });
  }
}
